import { XMarkIcon } from '@heroicons/react/24/solid'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HashLoader } from 'react-spinners'

import { colors } from '../../constants/colors'
import trackEvents from '../../constants/track-events'
import {
  useAivyContext,
  useLanguageContext,
  usePartnerContext,
  usePaymentContext
} from '../../context'
import { useFeatureGate } from '../../features/pricing/use-feature-gate'
import { trackEvent } from '../../helpers/analytics'
import { sortCrossComparison } from '../../helpers/sortings'
import Alert from '../alert'
import Paywall from '../paywall'
import UpgradeInline from '../pricing/upgrade-inline'
import Table from '../react-table/table'
import Button from '../tailwind/Button'
import PageHeading from '../tailwind/PageHeading'
import Slideover from '../tailwind/slideover'
import Tooltip from '../tooltip'
import VersionSwitch from '../version-switch'

const CROSS_MATCHING_FEATURE = 'CROSS_MATCHING'
const CROSS_MATCHING_SOURCE = 'talents_crossmode'

const {
  SHOW_DIAGNOSTIC_VERSION_1_CROSSMODE,
  SHOW_DIAGNOSTIC_VERSION_2_CROSSMODE,
  CLICK_REQUEST_CHALLENGES_CROSSMODE
} = trackEvents

// Helper function to map missing dimensions to challenges using system data
const mapDimensionsToChallenges = (
  missingDimensions,
  systemDimensions,
  systemChallenges,
  language
) => {
  if (!missingDimensions || !systemDimensions || !systemChallenges) return []

  const challenges = new Set()

  missingDimensions.forEach((dimensionKey) => {
    // Find the dimension in system data
    const dimension = Object.values(systemDimensions).find(
      (d) => d.key === dimensionKey
    )
    if (dimension && dimension.measured_by) {
      // Add all challenges that measure this dimension
      dimension.measured_by.forEach((examId) => {
        challenges.add(examId)
      })
    }
  })

  // Convert to localized names using system challenge data
  return Array.from(challenges).map((examId) => {
    const challenge = systemChallenges[examId]
    if (challenge && challenge.title && challenge.title[language]) {
      return challenge.title[language]
    }
    // Fallback to exam_id if no translation found
    return examId
  })
}

// Helper function to get missing challenges for a talent and career
const getMissingChallenges = async (spaceId, careerId, careersMap) => {
  try {
    // Get the career configuration
    const career = careersMap.get(careerId)
    if (!career?.app_settings?.challenges) return []

    // Get required challenges for this career
    const requiredChallenges = JSON.parse(career.app_settings.challenges).map(
      (c) => c.exam_id
    )

    // Get completed challenges for this talent
    const { query } = await import('../../graphql')
    const answersResponse = await query({
      query: 'answersBySpace',
      variables: { space_id: spaceId }
    })

    const completedChallenges = answersResponse.map((answer) => answer.exam_id)

    // Find missing challenges - return exam_ids directly since we don't have system data here
    const missingChallenges = requiredChallenges.filter(
      (challengeId) => !completedChallenges.includes(challengeId)
    )

    return missingChallenges
  } catch (error) {
    console.error('Error getting missing challenges:', error)
    return []
  }
}

const CrossmodeSlideover = ({
  open,
  handleClose,
  data,
  setSelectionState,
  crossmodeLoading,
  selectionState,
  matchBatchIfYouCan,
  setShowReinviteSpaceModal,
  careers_map
}) => {
  const { t } = useTranslation()
  const { partner } = usePartnerContext()
  const { crossComparison } = usePaymentContext()
  const { system } = useAivyContext()
  // Die Werte bleiben in jedem Modus der Platzhalter 'blur'. In enforced
  // ersetzt die Upgrade-Karte die statische Paywall, inline, weil der
  // Slideover selbst ein Dialog ist.
  const crossMatchingGate = useFeatureGate(CROSS_MATCHING_FEATURE, {
    source: CROSS_MATCHING_SOURCE
  })
  const { language } = useLanguageContext()

  const { isV1ScoreVisible, isV2ScoreVisible } = partner

  const activeCareers = data.careers.filter(
    ({ status, archived }) => status === 'ACTIVE' && !archived
  )

  const hasV1Careers = activeCareers.some(
    ({ diagnostic_version }) => diagnostic_version === 1
  )

  const hasV2Careers = activeCareers.some(
    ({ diagnostic_version }) => diagnostic_version === 2
  )

  const [activeDiagnosticVersion, setActiveDiagnosticVersion] = useState(2)
  const [missingChallengesCache, setMissingChallengesCache] = useState({})

  const versionedCareers =
    hasV1Careers && hasV2Careers
      ? activeCareers.filter(({ diagnostic_version }) => {
          return diagnostic_version === activeDiagnosticVersion
        })
      : activeCareers

  const scoreVisible =
    (activeDiagnosticVersion === 1 && isV1ScoreVisible) ||
    (activeDiagnosticVersion === 2 && isV2ScoreVisible)

  return (
    <Slideover open={open} setOpen={handleClose}>
      <PageHeading
        title={t('talents.crossmode.title')}
        withActions={
          <div className='flex items-center gap-x-4'>
            {hasV1Careers && hasV2Careers && (
              <div className='bg-white-50 flex justify-end'>
                <VersionSwitch
                  versions={[
                    {
                      version: 1,
                      active: activeDiagnosticVersion === 1,
                      handleChange: () => {
                        trackEvent(SHOW_DIAGNOSTIC_VERSION_1_CROSSMODE)
                        setActiveDiagnosticVersion(1)
                        setSelectionState({ career_ids: [] })
                      }
                    },
                    {
                      version: 2,
                      active: activeDiagnosticVersion === 2,
                      handleChange: () => {
                        trackEvent(SHOW_DIAGNOSTIC_VERSION_2_CROSSMODE)
                        setActiveDiagnosticVersion(2)
                        setSelectionState({ career_ids: [] })
                      }
                    }
                  ]}
                />
              </div>
            )}
            <button
              type='button'
              className={clsx(
                'flex items-center justify-center',
                'h-8 w-8 rounded-full bg-gray-200 hover:bg-gray-300',
                'group focus:outline-none'
              )}
              onClick={handleClose}
            >
              <span className='sr-only'>Close panel</span>
              <XMarkIcon
                className='h-6 w-6 text-gray-700 group-hover:text-gray-900'
                aria-hidden='true'
              />
            </button>
          </div>
        }
        sticky
      />
      {crossComparison ? null : crossMatchingGate.enforced ? (
        <div className='px-4 py-4 sm:px-6 lg:px-8'>
          <UpgradeInline
            feature={CROSS_MATCHING_FEATURE}
            source={CROSS_MATCHING_SOURCE}
          />
        </div>
      ) : (
        <div className='mb-4 py-4'>
          <Paywall type='static' paywall='general' />
        </div>
      )}
      <div className='bg-gray-100'>
        <div className='px-4 py-3 sm:px-6 md:flex md:items-center lg:px-8'>
          <div className='flex flex-wrap items-center gap-1'>
            {versionedCareers.map(({ id, title }) => {
              const { career_ids } = selectionState
              const active = career_ids.includes(id)

              const handleOnClick = () => {
                if (active) {
                  setSelectionState({
                    career_ids: career_ids.filter((activeID) => activeID !== id)
                  })
                } else {
                  if (career_ids.length === 3) return

                  setSelectionState({ career_ids: [...career_ids, id] })
                }
              }

              return (
                <span
                  key={id}
                  onClick={handleOnClick}
                  className={clsx(
                    'inline-flex cursor-pointer items-center rounded-full border px-3 py-1.5 text-sm font-medium',
                    active
                      ? 'border-gray-900 bg-gray-700 text-white'
                      : 'border-gray-200 bg-white text-gray-900'
                  )}
                >
                  <span>{title}</span>
                </span>
              )
            })}
          </div>
        </div>
      </div>
      {crossmodeLoading.current.length ? (
        <div className='mt-32 flex justify-center'>
          <HashLoader
            size={128}
            color={colors.carnationPink}
            speedMultiplier={0.8}
          />
        </div>
      ) : (
        <>
          {scoreVisible ? (
            <Table
              data={selectionState.spaces.map((space) => {
                const matching = {}

                const hasCompleteDataset = { current: true }

                Object.keys(matchBatchIfYouCan[space.owner] || {}).forEach(
                  (career_id) => {
                    const matchingData =
                      matchBatchIfYouCan[space.owner][career_id][0]
                    const {
                      matching_score,
                      complete_dataset,
                      missing_dimensions_for_matching
                    } = matchingData

                    // if there is only one score for a career
                    // with !complete_dataset, we should display "invite again"
                    if (!complete_dataset) hasCompleteDataset.current = false

                    if (complete_dataset) {
                      const value = crossComparison
                        ? Math.round(matching_score)
                        : 'blur'

                      matching[career_id] = { value, complete_dataset }
                    } else {
                      const value = t(
                        'talents.crossmode.complete_dataset_false'
                      )

                      matching[career_id] = {
                        value,
                        complete_dataset,
                        missing_dimensions_for_matching
                      }
                    }
                  }
                )

                return {
                  ...space,
                  ...matching,
                  hasCompleteDataset: hasCompleteDataset.current
                }
              })}
              columns={[
                {
                  accessorKey: 'identification',
                  accessorFn: (row) => row.identification || row.email,
                  enableSorting: false,
                  header: (
                    <span className='text-sm font-semibold text-gray-900'>
                      {t('talents.column_header_identification')}
                    </span>
                  ),
                  cell: ({ getValue, cell }) => {
                    const { id, hasCompleteDataset, all_challenges_unlocked } =
                      cell.row.original

                    const alreadyReinvited =
                      all_challenges_unlocked && !hasCompleteDataset

                    const showReinviteOption =
                      !all_challenges_unlocked && !hasCompleteDataset

                    return (
                      <div>
                        <span className='block text-sm font-medium text-gray-900'>
                          {getValue()}
                        </span>
                        {showReinviteOption && (
                          <Button.Text
                            onClick={() => {
                              trackEvent(CLICK_REQUEST_CHALLENGES_CROSSMODE)

                              handleClose()
                              setShowReinviteSpaceModal(id)
                            }}
                            text={t('talents.open_reinvite_space_modal_action')}
                          />
                        )}
                        {alreadyReinvited && (
                          <span className='text-xs italic text-gray-700'>
                            {t('talents.already_reinvited_hint')}
                          </span>
                        )}
                      </div>
                    )
                  }
                },
                ...selectionState.career_ids.map((id) => ({
                  accessorKey: id,
                  sortDescFirst: false,
                  sortingFn: sortCrossComparison,
                  header: (
                    <span className='text-sm font-semibold text-gray-900'>
                      {t('talents.crossmode.fit_to')}{' '}
                      {careers_map.get(id).title}
                    </span>
                  ),
                  cell: ({ getValue, row }) => {
                    const cellData = getValue() || {}
                    const {
                      complete_dataset,
                      value,
                      missing_dimensions_for_matching
                    } = cellData
                    const displayTooltip = !complete_dataset && value
                    const spaceOwner = row.original.owner
                    const careerId = id
                    const cacheKey = `${spaceOwner}-${careerId}`

                    // Create enhanced tooltip text
                    let tooltipText = t(
                      'talents.crossmode.complete_dataset_false_tooltip'
                    )

                    // Use backend data if available, otherwise fall back to frontend calculation
                    if (
                      missing_dimensions_for_matching &&
                      missing_dimensions_for_matching.length > 0
                    ) {
                      // Backend provides missing dimensions - map to challenges using system data
                      const systemDimensions = system?.dimensions
                        ? JSON.parse(system.dimensions.data)
                        : null
                      const systemChallenges = system?.challenges
                        ? JSON.parse(system.challenges.data)
                        : null
                      const missingChallengeNames = mapDimensionsToChallenges(
                        missing_dimensions_for_matching,
                        systemDimensions,
                        systemChallenges,
                        language
                      )

                      if (missingChallengeNames.length > 0) {
                        const challengeNames = missingChallengeNames.join(', ')
                        tooltipText += `<br/><br/><strong>${t('talents.crossmode.missing_challenges_label')}:</strong><br/>${challengeNames}`
                      }
                    } else {
                      // Fallback to frontend calculation
                      const missingChallenges =
                        missingChallengesCache[cacheKey] || []

                      // Load missing challenges if not cached and tooltip should be displayed
                      if (displayTooltip && !missingChallengesCache[cacheKey]) {
                        getMissingChallenges(
                          row.original.id,
                          careerId,
                          careers_map
                        ).then((missing) => {
                          setMissingChallengesCache((prev) => ({
                            ...prev,
                            [cacheKey]: missing
                          }))
                        })
                      }

                      if (missingChallenges.length > 0) {
                        const challengeNames = missingChallenges.join(', ')
                        tooltipText += `<br/><br/><strong>${t('talents.crossmode.missing_challenges_label')}:</strong><br/>${challengeNames}`
                      }
                    }

                    return (
                      <div className='flex items-center'>
                        <span
                          className={clsx(
                            'text-sm text-gray-500',
                            complete_dataset ? null : 'italic',
                            value === 'blur' ? 'blur' : null
                          )}
                        >
                          {value || t('talents.crossmode.no_owner')}
                        </span>
                        {displayTooltip && (
                          <div className='ml-2'>
                            <Tooltip
                              id={`complete_dataset_false_tooltip_${cacheKey}`}
                              place='top'
                              tip={tooltipText}
                            />
                          </div>
                        )}
                      </div>
                    )
                  }
                })),
                {
                  accessorKey: 'remove',
                  enableSorting: false,
                  header: '',
                  cell: ({ row }) => (
                    <XMarkIcon
                      onClick={row.original.removeSelected}
                      className='ml-auto h-5 w-5 text-gray-900 hover:cursor-pointer'
                    />
                  )
                }
              ]}
              disableHover
            />
          ) : (
            <div className='p-4 sm:p-6 lg:p-8'>
              <Alert
                className='max-w-xl'
                type='warning'
                title={t('talents.crossmode.score_not_available_title')}
                description={t(
                  'talents.crossmode.score_not_available_description'
                )}
              />
            </div>
          )}
        </>
      )}
      <div className='h-16' />
    </Slideover>
  )
}

CrossmodeSlideover.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
  setSelectionState: PropTypes.func.isRequired,
  crossmodeLoading: PropTypes.object.isRequired,
  selectionState: PropTypes.object.isRequired,
  matchBatchIfYouCan: PropTypes.object.isRequired,
  setShowReinviteSpaceModal: PropTypes.func.isRequired,
  careers_map: PropTypes.object.isRequired
}

export default CrossmodeSlideover
